import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  message: "",
  website: "", // honeypot — must remain empty
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setErrors((p) => ({ ...p, [e.target.name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 6) next.phone = "Enter a valid phone number.";
    if (form.message.trim().length < 5) next.message = "Tell us a little more about your enquiry.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        company: form.company || null,
        service: form.service || null,
        website: form.website || null,
      });
      toast.success("Thank you — your enquiry has been received.", {
        description: "Our team will revert to you shortly.",
      });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't submit your enquiry. Please try again or call us directly.";
      toast.error("Submission failed", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#FDFBF7] py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — copy + details */}
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-4 mb-6">
              <span className="gold-line" />
              <span className="font-sans text-[#C8A24A] uppercase text-xs tracking-widest-2">
                Get in Touch
              </span>
            </div>
            <h2 className="font-serif text-[#0B2545] text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Speak with our cargo desk.
            </h2>
            <p className="mt-6 font-sans text-[#061426]/75 text-base md:text-lg leading-relaxed">
              Whether it's a single consignment or a long-term port-handling
              partnership — share a few details and we'll get back within one
              working day.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex gap-4" data-testid="contact-address">
                <MapPin className="text-[#C8A24A] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-sans text-xs uppercase tracking-widest-2 text-[#0B2545]/55 mb-1">
                    Office
                  </div>
                  <div className="font-serif text-[#0B2545] text-lg leading-snug">
                    {CONTACT.address}
                  </div>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-phones">
                <Phone className="text-[#C8A24A] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-sans text-xs uppercase tracking-widest-2 text-[#0B2545]/55 mb-1">
                    Phone
                  </div>
                  {CONTACT.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="block font-serif text-[#0B2545] text-lg hover:text-[#C8A24A] transition-colors"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-email">
                <Mail className="text-[#C8A24A] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-sans text-xs uppercase tracking-widest-2 text-[#0B2545]/55 mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="font-serif text-[#0B2545] text-lg hover:text-[#C8A24A] transition-colors"
                  >
                    {CONTACT.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="bg-white border border-[#0B2545]/10 p-8 md:p-12 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.25)]"
            >
              {/* Honeypot — hidden from human users, attractive to bots */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label>
                  Website (leave blank)
                  <input
                    type="text"
                    name="website"
                    tabIndex="-1"
                    autoComplete="off"
                    value={form.website}
                    onChange={onChange}
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Full Name *" error={errors.name}>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="contact-input"
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email Address *" error={errors.email}>
                  <input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="contact-input"
                    placeholder="you@company.com"
                  />
                </Field>
                <Field label="Phone *" error={errors.phone}>
                  <input
                    data-testid="contact-input-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="contact-input"
                    placeholder="+91 ..."
                  />
                </Field>
                <Field label="Company">
                  <input
                    data-testid="contact-input-company"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className="contact-input"
                    placeholder="Organisation (optional)"
                  />
                </Field>
                <Field label="Service of Interest" full>
                  <select
                    data-testid="contact-input-service"
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    className="contact-input"
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="Message *" full error={errors.message}>
                  <textarea
                    data-testid="contact-input-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    className="contact-input resize-none"
                    placeholder="Tell us about your cargo, ports of interest, timelines…"
                  />
                </Field>
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="mt-8 inline-flex items-center gap-3 bg-[#0B2545] text-white hover:bg-[#C8A24A] hover:text-[#0B2545] disabled:opacity-60 disabled:cursor-not-allowed font-bold px-10 py-4 uppercase text-sm tracking-widest-2 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry <ArrowRight size={16} />
                  </>
                )}
              </button>
              <p className="mt-4 font-sans text-xs text-[#0B2545]/55">
                We respect your privacy. Your details are used solely to respond
                to your enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .contact-input {
          width: 100%;
          background: #FDFBF7;
          border: 1px solid rgba(11,37,69,0.15);
          padding: 14px 16px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          color: #061426;
          outline: none;
          transition: border-color .25s, background .25s;
        }
        .contact-input:focus {
          border-color: #C8A24A;
          background: #fff;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, error, full, children }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="block font-sans text-xs uppercase tracking-widest-2 text-[#0B2545]/65 mb-2">
      {label}
    </span>
    {children}
    {error && (
      <span className="block mt-1 text-xs text-red-700 font-sans" role="alert">
        {error}
      </span>
    )}
  </label>
);
